import React, { useState, useEffect } from "react";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import NoSsr from "@material-ui/core/NoSsr";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import UiDialogTitle from "../../common/UiDialogTitle";
import UiButton from "../../common/UiButton";
import UiDialogActions from "../../common/UiDialogActions";
import UiFileDropzone from "../../common/UiFileDropzone";
import UiProgress from "../../common/UiProgess";
import CreatableSelect from "react-select/creatable";
import {
  JSON_FILE_ACCEPT,
  JSON_FILE_TYPES_LABEL,
} from "../../import/allowedFileTypes";
import BatchType from "../../../types/Batch";
import constants from "../../../tools/constants";
import withContext from "../../../tools/withContext";
type PredictionModelApiItem = {
  modelId: string;
  modelName: string;
  modelVersion: string;
};

/** Réactiver la liste déroulante des modèles quand l’API / le besoin métier sera prêt */
const MODEL_SELECTION_ENABLED = true;

const INVALID_JSON_MESSAGE =
  "Le fichier doit contenir un tableau de predictions non vide : { predictions: [...] } ou directement [...].";

type SelectOption = {
  value: string;
  label: string;
};

type UploadedJsonData = {
  predictions: Array<{ id: string; labels: any }>;
};

const formatCreateLabelFr = (inputValue: string) =>
  `Créer "${inputValue}"`;

const isValidPredictionItem = (item: any) =>
  item && item.id != null && item.labels != null;

const isValidPredictionsArray = (predictions: any): predictions is any[] =>
  Array.isArray(predictions) &&
  predictions.length > 0 &&
  predictions.every(isValidPredictionItem);

/** { predictions: [...] } ou tableau à la racine [...] */
const extractPredictionsSource = (raw: any): any[] | null => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (!raw || typeof raw !== "object") {
    return null;
  }

  if (Array.isArray(raw.predictions)) {
    return raw.predictions;
  }

  const arrayValues = Object.values(raw).filter(Array.isArray);
  if (arrayValues.length === 1) {
    return arrayValues[0] as any[];
  }

  return null;
};

const parseUploadedJson = (
  raw: any
): { predictions: UploadedJsonData["predictions"]; modelName?: string; modelVersion?: string } => {
  const predictionsSource = extractPredictionsSource(raw);

  if (!isValidPredictionsArray(predictionsSource)) {
    throw new Error(INVALID_JSON_MESSAGE);
  }

  const predictions = predictionsSource.map((item: any) => ({
    id: String(item.id),
    labels: item.labels,
  }));

  const metadataSource =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  const modelName =
    metadataSource && typeof metadataSource.modelName === "string"
      ? metadataSource.modelName.trim()
      : "";
  const modelVersion =
    metadataSource && typeof metadataSource.modelVersion === "string"
      ? metadataSource.modelVersion.trim()
      : "";

  return {
    predictions,
    ...(modelName ? { modelName } : {}),
    ...(modelVersion ? { modelVersion } : {}),
  };
};

const styles = (theme: Theme) =>
  createStyles({
    fileDropzone: {
      marginTop: 0,
    },
    progressBlock: {
      marginTop: theme.spacing.unit * 3,
      textAlign: "center",
    },
    progressLabel: {
      marginTop: theme.spacing.unit,
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
    selectBlock: {
      marginBottom: theme.spacing.unit * 2,
    },
  });

export interface ApplyLabelingPayload {
  batchId: number;
  projectId: string;
  modelName: string;
  modelVersion: string;
  predictions: Array<{ id: string; labels: any }>;
}

interface PropsType {
  open: boolean;
  onClose: () => void;
  onApply: (
    payload: ApplyLabelingPayload,
    onProgress: (saved: number, total: number) => void
  ) => Promise<void>;
  batch: BatchType;
  classes: any;
  ctx: { appFetch: (url: string, options?: any) => Promise<any> };
}

const ApplyLabelingDialog = (props: PropsType) => {
  const { open, onClose, onApply, batch, classes, ctx } = props;
  const { appFetch } = ctx;

  const [modelName, setModelName] = useState("");
  const [modelVersion, setModelVersion] = useState("");
  const [models, setModels] = useState<PredictionModelApiItem[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [jsonData, setJsonData] = useState<UploadedJsonData | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonFileName, setJsonFileName] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState({ saved: 0, total: 0 });
  const [applyError, setApplyError] = useState<string | null>(null);

  const resetForm = () => {
    setModelName("");
    setModelVersion("");
    setJsonData(null);
    setJsonError(null);
    setJsonFileName(null);
    setModelsError(null);
    setApplying(false);
    setProgress({ saved: 0, total: 0 });
    setApplyError(null);
  };

  const fetchModels = async () => {
    const projectId = String(batch.project.id);
    setModelsLoading(true);
    setModelsError(null);
    try {
      const response = await appFetch(
        `${constants.paths.PREDICTION_MODELS_PATH}?projectId=${encodeURIComponent(
          projectId
        )}`
      );
      const list = response.content || response;
      const normalized: PredictionModelApiItem[] = (Array.isArray(list)
        ? list
        : []
      ).map((item: any) => ({
        modelId: String(item.modelId || item.id || ""),
        modelName: String(item.modelName || item.name || ""),
        modelVersion: String(item.modelVersion || item.version || ""),
      }));
      setModels(normalized);
    } catch (e) {
      setModels([]);
      setModelsError("Impossible de charger la liste des modèles");
    } finally {
      setModelsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    if (MODEL_SELECTION_ENABLED) {
      fetchModels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batch.project.id]);

  const onReaderLoad = (e: any) => {
    try {
      const obj = JSON.parse(e.target.result);
      const parsed = parseUploadedJson(obj);
      setJsonData({ predictions: parsed.predictions });
      if (parsed.modelName) {
        setModelName(parsed.modelName);
      }
      if (parsed.modelVersion) {
        setModelVersion(parsed.modelVersion);
      }
      setJsonError(null);
    } catch (err) {
      const message = (err as Error).message;
      setJsonError(
        message === INVALID_JSON_MESSAGE
          ? INVALID_JSON_MESSAGE
          : "Fichier JSON non valide"
      );
      setJsonData(null);
    }
  };

  const handleJsonDrop = (files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }
    setJsonFileName(file.name);
    const reader = new FileReader();
    reader.onload = onReaderLoad;
    reader.readAsText(file);
  };

  const modelNameOptions: SelectOption[] = Array.from(
    new Set(models.map((m) => m.modelName).filter(Boolean))
  ).map((name) => ({ value: name, label: name }));

  const versionOptions: SelectOption[] = Array.from(
    new Set(
      models
        .filter((m) => m.modelName === modelName)
        .map((m) => m.modelVersion)
        .filter(Boolean)
    )
  ).map((version) => ({ value: version, label: version }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!jsonData) {
      return;
    }

    setApplying(true);
    setApplyError(null);
    setProgress({ saved: 0, total: jsonData.predictions.length });

    try {
      await onApply(
        {
          batchId: batch.id,
          projectId: String(batch.project.id),
          modelName,
          modelVersion,
          predictions: jsonData.predictions,
        },
        (saved, total) => setProgress({ saved, total })
      );
    } catch (err) {
      setApplyError((err as Error).message);
      setApplying(false);
    }
  };

  const canApply =
    jsonData !== null &&
    modelName.trim() !== "" &&
    modelVersion.trim() !== "" &&
    !applying;
  const progressPercent =
    progress.total > 0 ? (progress.saved / progress.total) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={applying ? () => {} : onClose}
      aria-labelledby="apply-labeling-title"
      fullWidth
      maxWidth="sm"
    >
      <form onSubmit={handleSubmit}>
        <UiDialogTitle
          onCloseClick={applying ? () => {} : onClose}
          title="Appliquer une labellisation"
        />
        <DialogContent>
          {!applying && (
            <>
              {/*
                Sélection du modèle — désactivée (MODEL_SELECTION_ENABLED).
                Réactiver en passant MODEL_SELECTION_ENABLED à true.
              */}
              {MODEL_SELECTION_ENABLED && (
                <>
                  <div className={classes.selectBlock}>
                    <NoSsr>
                      <CreatableSelect
                        isClearable
                        isLoading={modelsLoading}
                        isDisabled={modelsLoading}
                        formatCreateLabel={formatCreateLabelFr}
                        noOptionsMessage={() => "Aucun résultat"}
                        loadingMessage={() => "Chargement..."}
                        placeholder="Modèle"
                        value={
                          modelName
                            ? { value: modelName, label: modelName }
                            : null
                        }
                        options={modelNameOptions}
                        onChange={(option: any) => {
                          const nextName = option ? String(option.value) : "";
                          setModelName(nextName);
                          if (
                            nextName !== modelName &&
                            !models.some(
                              (m) =>
                                m.modelName === nextName &&
                                m.modelVersion === modelVersion
                            )
                          ) {
                            setModelVersion("");
                          }
                        }}
                        onCreateOption={(inputValue: string) => {
                          setModelName(inputValue.trim());
                          setModelVersion("");
                        }}
                      />
                    </NoSsr>
                  </div>

                  <div className={classes.selectBlock}>
                    <NoSsr>
                      <CreatableSelect
                        isClearable
                        isLoading={modelsLoading}                      
                        isDisabled={modelsLoading || modelName.trim() === ""}
                        formatCreateLabel={formatCreateLabelFr}
                        noOptionsMessage={() => "Aucun résultat"}
                        loadingMessage={() => "Chargement..."}
                        placeholder="Version"
                        value={
                          modelVersion
                            ? { value: modelVersion, label: modelVersion }
                            : null
                        }
                        options={versionOptions}
                        onChange={(option: any) => {
                          setModelVersion(option ? String(option.value) : "");
                        }}
                        onCreateOption={(inputValue: string) => {
                          setModelVersion(inputValue.trim());
                        }}
                      />
                    </NoSsr>
                  </div>

                  {modelsError && (
                    <Typography color="error" variant="body2">
                      {modelsError}
                    </Typography>
                  )}
                </>
              )}

              <UiFileDropzone
                className={classes.fileDropzone}
                accept={JSON_FILE_ACCEPT}
                fieldLabel="Importer un fichier JSON"
                fieldRequired
                idlePrefix="Glisser-déposer un fichier JSON ou"
                chooseButtonLabel="choisir un fichier"
                selectedFileName={jsonFileName}
                onDrop={handleJsonDrop}
                footer={JSON_FILE_TYPES_LABEL}
                error={jsonError}
                metaText={
                  jsonData
                    ? `${jsonData.predictions.length} prédiction(s)${
                        modelName && modelVersion
                          ? ` — ${modelName} v${modelVersion}`
                          : ""
                      }`
                    : null
                }
              />
            </>
          )}

          {applying && (
            <div className={classes.progressBlock}>
              <Typography variant="body1">
                Application de la labellisation en cours...
              </Typography>
              <UiProgress
                variant="determinate"
                color="secondary"
                value={progressPercent}
              />
              <Typography className={classes.progressLabel}>
                {progress.saved} / {progress.total}
              </Typography>
            </div>
          )}

          {applyError && (
            <Typography color="error" variant="body2">
              {applyError}
            </Typography>
          )}
        </DialogContent>
        <UiDialogActions>
          <UiButton
            variant="outlined"
            color="primary"
            onClick={onClose}
            disabled={applying}
          >
            Annuler
          </UiButton>
          {!applying && (
            <UiButton
              variant="contained"
              color="secondary"
              type="submit"
              disabled={!canApply}
            >
              Appliquer
            </UiButton>
          )}
        </UiDialogActions>
      </form>
    </Dialog>
  );
};

export default withStyles(styles)(withContext(ApplyLabelingDialog));
